import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import '../../wallet.css';
import '../../wallet-fixes.css';
import { getDriverApiBaseUrl } from '../../utils/utils';
import {
    formatMoney, formatInt, formatDateTime,
    Person, Pill, StatCard, EmptyState, WalletSpinner,
    useWalletToasts, WalletToastStack, SignaturePad, WalletSelect,
} from './walletShared';

const FINANCE_OPTION = { value: '', label: 'Directly to finance (no accountant)' };

export default function SupervisorCollections() {
    const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/`;
    const email = sessionStorage.getItem('useremail') || '';
    const authHeaders = () => {
        const t = sessionStorage.getItem('usertoken');
        return t ? { Authorization: `Bearer ${t}` } : {};
    };
    const { toasts, toast, dismiss } = useWalletToasts();
    const navigate = useNavigate();

    const [wallet, setWallet] = useState(null);       // supervisor's own held cash
    const [drivers, setDrivers] = useState([]);       // drivers with outstanding
    const [txns, setTxns] = useState([]);             // supervisor ledger
    const [accountants, setAccountants] = useState([]); // accountants to hand over to
    const [loading, setLoading] = useState(true);

    // Collect modal
    const [modal, setModal] = useState(false);
    const [driver, setDriver] = useState(null);
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [signature, setSignature] = useState('');
    const [saving, setSaving] = useState(false);

    // Handover modal
    const [hoModal, setHoModal] = useState(false);
    const [hoNotes, setHoNotes] = useState('');
    const [hoAccountantId, setHoAccountantId] = useState('');  // '' = straight to finance
    const [hoSignature, setHoSignature] = useState('');

    const money = (v) => (v != null ? Number(v).toFixed(2) : '0.00');

    const [sigView, setSigView] = useState(null); // base64 signature being previewed

    // Label + styling for a ledger row by its type.
    const txnMeta = (type) => {
        switch (type) {
            case 'COLLECTED_FROM_DRIVER': return { label: 'Collected', tone: 'warning', icon: 'fa-arrow-down' };
            case 'HANDOVER_TO_ACCOUNTANT': return { label: 'To accountant', tone: 'primary', icon: 'fa-calculator' };
            case 'HANDOVER_TO_FINANCE': return { label: 'To finance', tone: 'primary', icon: 'fa-university' };
            case 'HANDOVER_TO_COMPANY': return { label: 'To company', tone: 'primary', icon: 'fa-university' };
            case 'RECEIVED_FROM_SUPERVISOR': return { label: 'Received', tone: 'warning', icon: 'fa-arrow-down' };
            default: return { label: type || '-', tone: 'default', icon: 'fa-exchange' };
        }
    };

    const loadAll = async () => {
        setLoading(true);
        try {
            const [w, b, t, a] = await Promise.all([
                axios.get(`${API_BASE}admin/wallet/supervisor/balance`, { params: { email }, headers: authHeaders() }),
                axios.get(`${API_BASE}admin/wallet/balances`, { headers: authHeaders() }),
                axios.get(`${API_BASE}admin/wallet/supervisor/transactions`, { params: { email }, headers: authHeaders() }),
                axios.get(`${API_BASE}admin/wallet/accountants`, { headers: authHeaders() }),
            ]);
            setWallet(w.data && w.data.data);
            setDrivers(((b.data && b.data.data) || []).filter(d => Number(d.outstanding) > 0));
            setTxns((t.data && t.data.data) || []);
            setAccountants((a.data && a.data.data) || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load supervisor wallet data.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

    // ── Collect from a driver ──────────────────────────────────────────────────
    const openCollect = (d) => { setDriver(d); setAmount(d.outstanding != null ? String(d.outstanding) : ''); setNotes(''); setSignature(''); setModal(true); };
    const closeCollect = () => setModal(false);
    const submitCollect = async () => {
        const amt = parseFloat(amount);
        if (isNaN(amt) || amt <= 0) return toast.error('Enter a valid amount.');
        if (!signature) return toast.error('Please sign to confirm the collection.');
        setSaving(true);
        try {
            const res = await axios.post(`${API_BASE}admin/wallet/${driver.driverId}/collect`,
                { amountCollected: amt, notes, signature, collectedByEmail: email },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            if (res.data && res.data.success) {
                toast.success(`Collected ${formatMoney(amt, driver.currency)} from ${driver.driverName || 'driver'}.`);
                setModal(false);
                loadAll();
            } else {
                toast.error((res.data && res.data.errors && res.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Failed to collect.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to submit collection.');
        } finally { setSaving(false); }
    };

    // ── Hand over to an accountant (or straight to finance) ────────────────────
    const openHandover = () => {
        setHoNotes('');
        setHoSignature('');
        setHoAccountantId(accountants.length ? String(accountants[0].userId) : '');
        setHoModal(true);
    };
    const closeHandover = () => setHoModal(false);
    const submitHandover = async () => {
        const amt = held;
        if (!(amt > 0)) return toast.error('No cash held to hand over.');
        if (!hoSignature) return toast.error('Please sign to confirm the handover.');
        const acc = accountants.find(a => String(a.userId) === String(hoAccountantId));
        setSaving(true);
        try {
            const res = await axios.post(`${API_BASE}admin/wallet/supervisor/handover`,
                {
                    amount: amt, notes: hoNotes, email, signature: hoSignature,
                    accountantUserId: acc ? acc.userId : null,
                    accountantEmail: acc ? acc.email : null,
                },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            if (res.data && res.data.success) {
                toast.success(`Handed over ${formatMoney(amt, currency)} to ${acc ? (acc.personnelName || acc.email) : 'finance'}.`);
                setHoModal(false);
                loadAll();
            } else {
                toast.error((res.data && res.data.errors && res.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Failed to hand over.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to submit handover.');
        } finally { setSaving(false); }
    };

    const held = wallet ? Number(wallet.held || 0) : 0;
    const currency = (wallet && wallet.currency) || 'AED';

    const totalOutstandingAmongDrivers = useMemo(
        () => drivers.reduce((s, d) => s + (Number(d.outstanding) || 0), 0),
        [drivers]
    );

    const accountantOptions = useMemo(() => ([
        ...accountants.map(a => ({ value: String(a.userId), label: `${a.personnelName || a.email} — Accountant` })),
        FINANCE_OPTION,
    ]), [accountants]);

    return (
        <div className="wallet-page wui">
            <WalletToastStack toasts={toasts} onDismiss={dismiss} />

            <div className="w-topbar">
                <div className="w-topbar-left">
                    <div className="w-icon-badge"><i className="fa fa-user-secret" aria-hidden="true" /></div>
                    <div>
                        <h2 className="w-title">Supervisor Collections</h2>
                        <p className="w-subtitle">
                            Collect COD cash from drivers (with signature), then hand it over to finance. Signed in as <strong>{email || 'unknown'}</strong>.
                        </p>
                    </div>
                </div>
                <div className="w-topbar-actions">
                    <button className="w-btn w-btn-primary" onClick={() => navigate('/orders/awb-custody?tab=from-driver')}>
                        <i className="fa fa-exchange" aria-hidden="true" /> Collect from driver (by AWB)
                    </button>
                    <button className="w-btn w-btn-ghost" onClick={loadAll} disabled={loading}>
                        <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
                    </button>
                </div>
            </div>

            {/* Supervisor wallet cards */}
            <div className="w-stat-grid">
                <StatCard
                    icon="fa-lock" label="Cash You Are Holding" value={`${held.toFixed(2)} ${currency}`} accent
                    sub="Held cash now moves by AWB — an accountant collects it from you in the AWB custody flow."
                />
                <StatCard
                    icon="fa-truck" label="Drivers to Collect From" value={formatInt(drivers.length)}
                    tone={drivers.length ? 'danger' : 'success'}
                    sub={drivers.length ? `${formatMoney(totalOutstandingAmongDrivers, currency)} outstanding in total` : 'All settled up'}
                />
            </div>

            {/* Drivers with outstanding COD */}
            <div className="w-card">
                <div className="w-card-header">
                    <span className="w-card-title"><i className="fa fa-truck" aria-hidden="true" /> Drivers holding COD cash</span>
                    <span className="w-card-meta">{formatInt(drivers.length)} driver(s)</span>
                </div>
                <div className="w-card-body">
                    {loading ? <WalletSpinner label="Loading…" /> : drivers.length === 0 ? (
                        <EmptyState icon="fa-check-circle" title="No drivers have outstanding cash right now" />
                    ) : (
                        <div className="w-rtable">
                            <table className="table table-sm align-middle w-rtable-table" style={{ marginTop: 4, marginBottom: 0 }}>
                                <thead><tr><th>Driver</th><th className="text-end">Outstanding</th><th></th></tr></thead>
                                <tbody>
                                    {drivers.map(d => (
                                        <tr key={d.driverId}>
                                            <td data-label="Driver"><Person name={d.driverName || `Driver #${d.driverId}`} sub={d.phoneNumber || `#${d.driverId}`} /></td>
                                            <td data-label="Outstanding" className="text-end"><Pill tone="danger">{formatMoney(d.outstanding, d.currency)}</Pill></td>
                                            <td className="text-end">
                                                <button className="w-btn w-btn-success w-btn-sm" onClick={() => navigate('/orders/awb-custody?tab=from-driver')}>
                                                    <i className="fa fa-exchange" aria-hidden="true" /> Collect by AWB
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Supervisor ledger */}
            <div className="w-card">
                <div className="w-card-header">
                    <span className="w-card-title"><i className="fa fa-exchange" aria-hidden="true" /> Your collection &amp; handover history</span>
                    <span className="w-card-meta">{formatInt(txns.length)} entries</span>
                </div>
                <div className="w-card-body">
                    {txns.length === 0 ? <EmptyState icon="fa-history" title="No activity yet" /> : (
                        <div className="w-rtable">
                            <table className="table table-sm align-middle w-rtable-table" style={{ marginTop: 4, marginBottom: 0 }}>
                                <thead><tr><th>Date</th><th>Type</th><th>Driver / Party</th><th className="text-end">Amount</th><th className="text-end">Held After</th><th>Note</th><th>Signature</th></tr></thead>
                                <tbody>
                                    {txns.map(t => {
                                        const m = txnMeta(t.type);
                                        const party = t.driverName || (t.driverId ? `#${t.driverId}` : (t.counterpartyEmail || '-'));
                                        return (
                                            <tr key={t.id}>
                                                <td data-label="Date" style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12.5 }}>{formatDateTime(t.createdAt)}</td>
                                                <td data-label="Type"><Pill tone={m.tone} icon={m.icon}>{m.label}</Pill></td>
                                                <td data-label="Driver / Party">{party}</td>
                                                <td data-label="Amount" className="text-end" style={{ fontWeight: 600 }}>{money(t.amount)} {t.currency}</td>
                                                <td data-label="Held After" className="text-end">{money(t.balanceAfter != null ? Math.abs(t.balanceAfter) : 0)}</td>
                                                <td data-label="Note" style={{ color: '#6b7280' }}>{t.description || '-'}</td>
                                                <td data-label="Signature">
                                                    {t.signature ? (
                                                        <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => setSigView(t.signature)}>
                                                            <i className="fa fa-pencil" aria-hidden="true" /> View
                                                        </button>
                                                    ) : <span style={{ color: '#adb5bd' }}>—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Collect modal */}
            {modal && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content" style={{ maxWidth: 500 }}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-money" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Collect from {driver?.driverName || 'driver'}</h3>
                                <p>Signature required to confirm the handoff</p>
                            </div>
                            <button className="close-btn" onClick={closeCollect}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div className="w-summary-line">
                                <span>Outstanding</span>
                                <strong style={{ color: '#e11d48' }}>{formatMoney(driver?.outstanding, driver?.currency)}</strong>
                            </div>
                            <div className="form-group">
                                <div className="w-field-row">
                                    <label style={{ margin: 0 }}>Amount received</label>
                                    <button type="button" className="w-link-btn"
                                        onClick={() => setAmount(driver?.outstanding != null ? String(driver.outstanding) : '')}>
                                        Use full amount
                                    </button>
                                </div>
                                <div className="w-amount-input">
                                    <span>{driver?.currency || 'AED'}</span>
                                    <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
                                </div>
                            </div>
                            <div className="form-group"><label>Notes</label><input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" /></div>
                            <div className="form-group"><label>Signature (supervisor — received)</label><SignaturePad onChange={setSignature} hint="Sign above to confirm you received the cash." /></div>
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={closeCollect} disabled={saving}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={submitCollect} disabled={saving || !amount}>
                                {saving ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Submitting…</> : <><i className="fa fa-check" aria-hidden="true" /> Submit</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Handover modal */}
            {hoModal && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content" style={{ maxWidth: 440 }}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-university" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Hand over cash</h3>
                                <p>Hand your held cash to an accountant (signed)</p>
                            </div>
                            <button className="close-btn" onClick={closeHandover}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div className="w-summary-line">
                                <span>You are holding</span>
                                <strong>{held.toFixed(2)} {currency}</strong>
                            </div>
                            <div className="form-group">
                                <label>Hand over to</label>
                                <WalletSelect
                                    options={accountantOptions}
                                    value={accountantOptions.find(o => o.value === hoAccountantId) || FINANCE_OPTION}
                                    onChange={(opt) => setHoAccountantId(opt.value)}
                                    isClearable={false}
                                    aria-label="Hand over to" />
                                {accountants.length === 0 && (
                                    <small style={{ color: '#b45309' }}>
                                        <i className="fa fa-exclamation-triangle" aria-hidden="true" /> No accountants set up yet — add one in <strong>Wallet Setup</strong>, or hand straight to finance.
                                    </small>
                                )}
                            </div>
                            <div className="form-group">
                                <label style={{ margin: 0 }}>Amount to hand over</label>
                                <div className="w-amount-input" style={{ background: '#f8fafc' }}>
                                    <span>{currency}</span>
                                    <strong style={{ padding: '6px 0' }}>{held.toFixed(2)}</strong>
                                </div>
                                <small style={{ color: '#64748b' }}>The full amount you hold is always handed over — no partial amounts.</small>
                            </div>
                            <div className="form-group"><label>Notes</label><input type="text" value={hoNotes} onChange={e => setHoNotes(e.target.value)} placeholder="e.g. reference / shift" /></div>
                            <div className="form-group">
                                <label>Signature (you — handing over)</label>
                                <SignaturePad onChange={setHoSignature} hint="Sign above to confirm you handed over the cash." />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={closeHandover} disabled={saving}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={submitHandover} disabled={saving || !(held > 0)}>
                                {saving ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Submitting…</> : <><i className="fa fa-check" aria-hidden="true" /> Confirm handover</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Signature preview */}
            {sigView && (
                <div className="modal-overlay w-modal" onClick={() => setSigView(null)}>
                    <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-pencil" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}><h3>Signature</h3><p>Captured at the time of the transaction</p></div>
                            <button className="close-btn" onClick={() => setSigView(null)}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body" style={{ textAlign: 'center' }}>
                            <img src={sigView} alt="signature" style={{ maxWidth: '100%', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }} />
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={() => setSigView(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
